import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { CartItem } from '../components/restaurant/CartItem';
import { CheckoutUpsellModal } from '../components/restaurant/CheckoutUpsellModal';
import { EmiliusCheckoutUpsellModal } from '../components/restaurant/EmiliusCheckoutUpsellModal';
import { useCart } from '../lib/cart';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useLanguage } from '../lib/i18n';
import { useTableOrder } from '../lib/table-context';
import { useRestaurantScheduleStatus } from '../lib/schedule';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase';
import { ShoppingBag, ArrowLeft, Store, CheckCircle2, Loader2, Utensils, AlertCircle } from 'lucide-react';
export function CartPage() {
  const navigate = useNavigate();
  // La ruta /r/:slug/cart SÍ expone el slug aunque el carrito esté vacío.
  // Es la señal MÁS confiable para saber que estamos en el flujo de
  // Emilius (cuando el carrito se vacía perdemos cart.restaurantId/slug,
  // pero la URL sigue diciendo emilius-pizza).
  const {
    slug: urlSlug
  } = useParams<{
    slug: string;
  }>();
  const {
    cart,
    updateQuantity,
    removeItem,
    getTotal,
    clearCart,
    getDiscount
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    t
  } = useLanguage();
  const {
    tableOrder,
    isTableOrder,
    clearTableOrder
  } = useTableOrder();
  // Schedule-based open/closed enforcement
  const scheduleStatus = useRestaurantScheduleStatus(cart.restaurantSlug);
  const isClosedBySchedule = scheduleStatus.isClosedForOrdering;
  // QR self-service dine-in restaurants (NatuCafé id 19, Rancho id 28).
  // When the customer scanned a table QR, the checkout flow is simplified
  // and the cart button reflects "send to kitchen" rather than "pay".
  const isQrSelfServiceTable = isTableOrder && !!tableOrder && (String(tableOrder.restaurantId) === '19' || String(tableOrder.restaurantId) === '28');
  // QR self-service ahora cubre Rancho (28) y NatuCafé (19) — ambos
  // usan one-tap submit desde el botón "Enviar Pedido". Se gating
  // hace con isQrSelfServiceTable arriba; no necesitamos un flag
  // exclusivo a Rancho aquí.
  // ─── Crack Pollo (id 20) upsell modal ────────────────────────────────
  const isCrackPolloCart = String(cart.restaurantId) === '20';
  // ─── Emilius Pizza (id 27) upsell modal ──────────────────────────────
  // Incluímos el slug como respaldo: aunque el carrito quede vacío y se
  // pierda restaurantId, mientras conservemos el slug seguimos sabiendo
  // que estamos en el flujo white-label de Emilius (para no mandar al
  // cliente al marketplace de NOIZA desde el carrito vacío).
  const isEmiliusCart = String(cart.restaurantId) === '27' || cart.restaurantSlug === 'emilius-pizza' || urlSlug === 'emilius-pizza';
  // ─── Kyomu Sushi (id 21) upsell modal ────────────────────────────────
  const isKyomuCart = String(cart.restaurantId) === '21';
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [showEmiliusUpsellModal, setShowEmiliusUpsellModal] = useState(false);
  const [showKyomuUpsellModal, setShowKyomuUpsellModal] = useState(false);
  const [upsellAlreadyShown, setUpsellAlreadyShown] = useState(false);
  // ── Rancho QR direct-submit state ─────────────────────────────────────
  const [ranchoSubmitState, setRanchoSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [ranchoOrderNumber, setRanchoOrderNumber] = useState<string | null>(null);
  const [ranchoSubmitError, setRanchoSubmitError] = useState<string | null>(null);
  // Snapshot of the table name + items at submit time, so the success
  // screen survives clearCart() / clearTableOrder().
  const [ranchoSuccessSnapshot, setRanchoSuccessSnapshot] = useState<{
    tableName: string;
    items: typeof cart.items;
    total: number;
  } | null>(null);
  // Cart totals (declared early so submitRanchoQrOrder can reference them).
  const subtotal = getTotal();
  const discount = getDiscount();
  const submitRanchoQrOrder = async () => {
    if (!tableOrder || !cart.restaurantId || cart.items.length === 0) return;
    if (ranchoSubmitState === 'submitting') return;
    setRanchoSubmitState('submitting');
    setRanchoSubmitError(null);
    try {
      const orderIntentId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `rancho-qr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const itemsPayload = cart.items.map(item => ({
        menu_item_id: item.menu_item_id,
        item_name_snapshot: item.item_name_snapshot,
        unit_price_snapshot: item.unit_price_snapshot,
        quantity: item.quantity,
        notes: item.notes || null,
        selected_options: item.selected_options.map(opt => ({
          option_group: opt.option_group || '',
          option_name: opt.option_name,
          price_delta: opt.price_delta
        }))
      }));
      // Dine-in QR self-service: no contact info, no payment, no
      // delivery fee. The kitchen ticket is identified by the table name.
      const payload = {
        order_intent_id: orderIntentId,
        force_new_order: false,
        restaurant_id: Number(cart.restaurantId),
        customer_name: tableOrder.tableName || `Mesa ${tableOrder.tableNumber}`,
        customer_phone: null,
        customer_email: null,
        delivery_type: 'dine_in',
        payment_method: 'cash',
        delivery_address: null,
        delivery_lat: null,
        delivery_lng: null,
        maps_url: null,
        table_id: tableOrder.tableId,
        notes: null,
        subtotal,
        delivery_fee: 0,
        total: subtotal,
        source: 'qr',
        wants_invoice: false,
        items: itemsPayload
      };
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) {
        // The recent-order 409 confirmation flow doesn't apply to dine-in
        // QR self-service (no phone, so the recent-order guard is skipped
        // server-side anyway). Anything else here is a real failure.
        const errMsg = result?.error || result?.message || `Error del servidor (${response.status})`;
        throw new Error(errMsg);
      }
      const order = result?.order || result;
      const orderNumber = order?.order_number || '';
      const snapshotItems = [...cart.items];
      const snapshotTableName = tableOrder.tableName || `Mesa ${tableOrder.tableNumber}`;
      const snapshotTotal = subtotal;
      setRanchoOrderNumber(String(orderNumber || ''));
      setRanchoSuccessSnapshot({
        tableName: snapshotTableName,
        items: snapshotItems,
        total: snapshotTotal
      });
      setRanchoSubmitState('success');
      // Clear cart + table context AFTER snapshot so the success screen
      // still shows the order summary.
      clearCart();
      clearTableOrder();
    } catch (err: any) {
      console.error('[CartPage][RanchoQR] submit error:', err);
      setRanchoSubmitError(err?.message || 'No pudimos enviar tu pedido. Inténtalo de nuevo o llama al mesero.');
      setRanchoSubmitState('error');
    }
  };
  const handleCheckoutClick = () => {
    if (isClosedBySchedule) {
      alert(`${cart.restaurantName || 'El restaurante'} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
      return;
    }
    // QR self-service dine-in: one-tap submit straight to the kitchen.
    // Cubre Rancho (28) y NatuCafé (19) — ambos usan la misma lógica:
    // sin formulario de contacto, sin pago, el ticket va directo a la
    // estación de cocina vía el trigger de print jobs y la orden
    // aparece en el KDS con su mesa.
    if (isQrSelfServiceTable) {
      submitRanchoQrOrder();
      return;
    }
    if (cart.items.length > 0 && !upsellAlreadyShown) {
      if (isCrackPolloCart) {
        setShowUpsellModal(true);
        return;
      }
      if (isEmiliusCart) {
        setShowEmiliusUpsellModal(true);
        return;
      }
      if (isKyomuCart) {
        setShowKyomuUpsellModal(true);
        return;
      }
    }
    navigate('/checkout');
  };
  const handleUpsellContinue = () => {
    setShowUpsellModal(false);
    setShowEmiliusUpsellModal(false);
    setShowKyomuUpsellModal(false);
    setUpsellAlreadyShown(true);
    navigate('/checkout');
  };
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (cart.restaurantId) {
      console.log('[NOIZA][CartPage] Registering restaurant:', cart.restaurantId);
      monitorRestaurant(cart.restaurantId);
    }
  }, [cart.restaurantId, monitorRestaurant]);
  // La Cabaña Parrillera (id=24) charges the delivery fee manually from
  // the KDS once the order is taken — it's never quoted in the public
  // cart, so showing a "Costo de envío" line here just confuses the
  // customer (especially because they haven't even chosen delivery yet).
  const isCabanaParrilleraCart = String(cart.restaurantId) === '24';
  // In a real app, delivery fee would be calculated based on distance/settings
  // For Rancho QR (dine-in self-service), never show a delivery fee.
  const deliveryFeeEstimate = isCabanaParrilleraCart || isQrSelfServiceTable || cart.items.length === 0 ? 0 : 1500;
  const total = subtotal + deliveryFeeEstimate;
  // ── Success state (Rancho QR) ────────────────────────────────────────
  if (ranchoSubmitState === 'success' && ranchoSuccessSnapshot) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-6289">
        <MarketplaceHeader data-id="element-6290" />
        <main className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-12" data-id="element-6291">
          <div className="bg-white rounded-3xl border border-emerald-100 shadow-sm overflow-hidden" data-id="element-6292">
            <div className="bg-emerald-50 px-6 py-8 text-center border-b border-emerald-100" data-id="element-6293">
              <div className="w-16 h-16 rounded-full bg-emerald-500 mx-auto mb-4 flex items-center justify-center" data-id="element-6294">
                <CheckCircle2 className="w-9 h-9 text-white" data-id="element-6295" />
              </div>
              <h1 className="text-2xl font-bold text-emerald-900 mb-1" data-id="element-6296">
                ¡Pedido enviado!
              </h1>
              <p className="text-emerald-700" data-id="element-6297">
                Tu pedido se está preparando en cocina.
              </p>
            </div>
            <div className="px-6 py-6 space-y-4" data-id="element-6298">
              <div className="flex items-center gap-3 bg-emerald-50/60 rounded-2xl px-4 py-3" data-id="element-6299">
                <Utensils className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-6300" />
                <div data-id="element-6301">
                  <p className="font-bold text-gray-900" data-id="element-6302">
                    {ranchoSuccessSnapshot.tableName}
                  </p>
                  {ranchoOrderNumber && <p className="text-sm text-gray-600" data-id="element-6303">
                      Pedido #{ranchoOrderNumber}
                    </p>}
                </div>
              </div>
              <div className="border border-gray-100 rounded-2xl divide-y divide-gray-100 overflow-hidden" data-id="element-6304">
                {ranchoSuccessSnapshot.items.map(item => <div key={item.id} className="px-4 py-3 flex justify-between items-start gap-4" data-id="element-6305">
                    <div data-id="element-6306">
                      <p className="font-medium text-gray-900" data-id="element-6307">
                        {item.quantity} × {item.item_name_snapshot}
                      </p>
                      {item.selected_options.length > 0 && <ul className="mt-1 text-xs text-gray-500 space-y-0.5" data-id="element-6308">
                          {item.selected_options.map((opt, idx) => <li key={idx} data-id="element-6309">· {opt.option_name}</li>)}
                        </ul>}
                    </div>
                    <span className="text-sm font-semibold text-gray-700 whitespace-nowrap" data-id="element-6310">
                      {formatCurrency((item.unit_price_snapshot + item.selected_options.reduce((sum, opt) => sum + (opt.price_delta || 0), 0)) * item.quantity)}
                    </span>
                  </div>)}
              </div>
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2" data-id="element-6311">
                <span data-id="element-6312">Total</span>
                <span data-id="element-6313">{formatCurrency(ranchoSuccessSnapshot.total)}</span>
              </div>
              <p className="text-sm text-gray-500 text-center pt-2" data-id="element-6314">
                Si necesitás algo, llamá al mesero. ¡Buen provecho!
              </p>
              <Button fullWidth onClick={() => navigate('/')} className="h-12" data-id="element-6315">
                Volver al inicio
              </Button>
            </div>
          </div>
        </main>
        <Footer data-id="element-6316" />
      </div>;
  }
  if (cart.items.length === 0) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-6317">
        <MarketplaceHeader data-id="element-6318" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-6319">
          <EmptyState icon={ShoppingBag} title={t('cart_empty')} description={t('cart_empty_desc')} actionLabel={isEmiliusCart ? 'Volver al menú' : t('explore')} onAction={() => navigate(isEmiliusCart ? `/r/${urlSlug || cart.restaurantSlug || 'emilius-pizza'}` : '/restaurants')} data-id="element-6320" />
        </main>
        <Footer data-id="element-6321" />
      </div>;
  }
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-6322">
      <MarketplaceHeader data-id="element-6323" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8" data-id="element-6324">
        <div className="flex items-center justify-between mb-8" data-id="element-6325">
          <div className="flex items-center gap-4" data-id="element-6326">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-6327">
              <ArrowLeft className="w-6 h-6" data-id="element-6328" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-6329">
              {t('cart_title')}
            </h1>
          </div>
          <button onClick={clearCart} className="text-sm font-medium text-red-500 hover:text-red-600 transition-colors" data-id="element-6330">
            {t('cart_clear')}
          </button>
        </div>

        {/* QR self-service table banner — Rancho (28) y NatuCafé (19) */}
        {isQrSelfServiceTable && tableOrder && <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 mb-6 flex items-center gap-3" data-id="element-6331">
            <Utensils className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-6332" />
            <div data-id="element-6333">
              <p className="font-bold text-emerald-900" data-id="element-6334">
                {tableOrder.tableName || `Mesa ${tableOrder.tableNumber}`} ·
                Autoatención
              </p>
              <p className="text-sm text-emerald-700" data-id="element-6335">
                Tu pedido se enviará directamente a la cocina.
              </p>
            </div>
          </div>}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-8" data-id="element-6336">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3" data-id="element-6337">
            <Store className="w-5 h-5 text-gray-500" data-id="element-6338" />
            <span className="font-semibold text-gray-900" data-id="element-6339">
              {cart.restaurantName}
            </span>
          </div>

          <div className="px-6 py-2" data-id="element-6340">
            {cart.items.map(item => <CartItem key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} data-id="element-6341" />)}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-6342">
          <div className="space-y-4" data-id="element-6343">
            <div className="flex justify-between text-gray-600" data-id="element-6344">
              <span data-id="element-6345">{t('subtotal')}</span>
              <span data-id="element-6346">{formatCurrency(subtotal + (discount?.amount ?? 0))}</span>
            </div>
            {discount && <div className="flex justify-between text-emerald-600 font-medium" data-id="element-6347">
                <span data-id="element-6348">{discount.label}</span>
                <span data-id="element-6349">-{formatCurrency(discount.amount)}</span>
              </div>}
            {deliveryFeeEstimate > 0 && <div className="flex justify-between text-gray-600" data-id="element-6350">
                <span data-id="element-6351">{t('delivery_fee')}</span>
                <span data-id="element-6352">{formatCurrency(deliveryFeeEstimate)}</span>
              </div>}
            <div className="pt-4 border-t border-gray-100 flex justify-between text-lg font-bold text-gray-900" data-id="element-6353">
              <span data-id="element-6354">{t('total')}</span>
              <span data-id="element-6355">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        {/* Error banner for QR self-service direct-submit (Rancho + NatuCafé) */}
        {isQrSelfServiceTable && ranchoSubmitState === 'error' && ranchoSubmitError && <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 mb-4 flex items-start gap-3" data-id="element-6356">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-6357" />
              <div className="flex-1" data-id="element-6358">
                <p className="font-semibold text-red-900" data-id="element-6359">
                  No pudimos enviar tu pedido
                </p>
                <p className="text-sm text-red-700 mt-0.5" data-id="element-6360">
                  {ranchoSubmitError}
                </p>
              </div>
            </div>}

        <Button onClick={handleCheckoutClick} className="w-full h-14 text-lg" disabled={isKitchenPaused || isClosedBySchedule || ranchoSubmitState === 'submitting'} data-id="element-6361">
          {ranchoSubmitState === 'submitting' ? <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-6362" />
              Enviando a cocina...
            </> : isClosedBySchedule ? `🔒 Cerrado${scheduleStatus.nextOpeningHint ? ` — ${scheduleStatus.nextOpeningHint}` : ''}` : isKitchenPaused ? t('kitchen_paused') : isQrSelfServiceTable ? 'Enviar Pedido' : t('cart_checkout')}
        </Button>
      </main>

      {isCrackPolloCart && cart.restaurantId && cart.restaurantName && <CheckoutUpsellModal isOpen={showUpsellModal} onClose={() => setShowUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} data-id="element-6363" />}

      {isEmiliusCart && cart.restaurantId && cart.restaurantName && <EmiliusCheckoutUpsellModal isOpen={showEmiliusUpsellModal} onClose={() => setShowEmiliusUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} data-id="element-6364" />}

      {isKyomuCart && cart.restaurantId && cart.restaurantName && <CheckoutUpsellModal isOpen={showKyomuUpsellModal} onClose={() => setShowKyomuUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} categorySlugs={['extras', 'adicionales', 'salsas-extra', 'bebidas']} title="¿Deseas agregar algo más?" subtitle="Acompaña tu sushi con extras o bebidas:" data-id="element-6365" />}

      <Footer data-id="element-6366" />
    </div>;
}